import { create } from 'zustand';
export const useAppStore = create((set, get) => ({
    // 初始状态
    appReady: false,
    loading: false,
    error: null,
    orders: [],
    users: [],
    statistics: null,
    sidebarCollapsed: false,
    theme: 'light',
    language: 'zh-CN',
    notifications: [],
    unreadNotifications: 0,
    // 初始化应用
    initializeApp: async () => {
        set({ loading: true });
        try {
            // 这里可以加载应用初始化需要的数据
            // 例如：用户信息、配置、权限等
            // 模拟初始化过程
            await new Promise((resolve) => setTimeout(resolve, 500));
            set({
                appReady: true,
                loading: false,
            });
        }
        catch (error) {
            set({
                error: error.message || '应用初始化失败',
                loading: false,
                appReady: true,
            });
            throw error;
        }
    },
    // 设置应用就绪状态
    setAppReady: (ready) => {
        set({ appReady: ready });
    },
    // 设置加载状态
    setLoading: (loading) => {
        set({ loading });
    },
    // 设置错误
    setError: (error) => {
        set({ error });
    },
    // 清除错误
    clearError: () => {
        set({ error: null });
    },
    // 订单管理
    setOrders: (orders) => {
        set({ orders });
    },
    addOrder: (order) => {
        set((state) => ({
            orders: [order, ...state.orders],
        }));
    },
    updateOrder: (orderId, updates) => {
        set((state) => ({
            orders: state.orders.map((order) => order.baseInfo.id === orderId
                ? { ...order, ...updates }
                : order),
        }));
    },
    deleteOrder: (orderId) => {
        set((state) => ({
            orders: state.orders.filter((order) => order.baseInfo.id !== orderId),
        }));
    },
    // 用户管理
    setUsers: (users) => {
        set({ users });
    },
    addUser: (user) => {
        set((state) => ({
            users: [user, ...state.users],
        }));
    },
    updateUser: (userId, updates) => {
        set((state) => ({
            users: state.users.map((user) => user.id === userId ? { ...user, ...updates } : user),
        }));
    },
    deleteUser: (userId) => {
        set((state) => ({
            users: state.users.filter((user) => user.id !== userId),
        }));
    },
    // UI控制
    toggleSidebar: () => {
        set((state) => ({
            sidebarCollapsed: !state.sidebarCollapsed,
        }));
    },
    setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
    },
    toggleTheme: () => {
        set((state) => ({
            theme: state.theme === 'light' ? 'dark' : 'light',
        }));
    },
    setTheme: (theme) => {
        set({ theme });
    },
    setLanguage: (language) => {
        set({ language });
    },
    // 通知管理
    addNotification: (notification) => {
        const newNotification = {
            ...notification,
            id: notification.id || Date.now().toString(),
            timestamp: notification.timestamp || new Date(),
            read: false,
        };
        set((state) => ({
            notifications: [newNotification, ...state.notifications],
            unreadNotifications: state.unreadNotifications + 1,
        }));
    },
    removeNotification: (id) => {
        set((state) => {
            const notification = state.notifications.find((n) => n.id === id);
            const wasUnread = notification && !notification.read;
            return {
                notifications: state.notifications.filter((n) => n.id !== id),
                unreadNotifications: wasUnread
                    ? state.unreadNotifications - 1
                    : state.unreadNotifications,
            };
        });
    },
    markNotificationAsRead: (id) => {
        set((state) => {
            const updatedNotifications = state.notifications.map((notification) => notification.id === id ? { ...notification, read: true } : notification);
            const unreadCount = updatedNotifications.filter((n) => !n.read).length;
            return {
                notifications: updatedNotifications,
                unreadNotifications: unreadCount,
            };
        });
    },
    markAllNotificationsAsRead: () => {
        set((state) => ({
            notifications: state.notifications.map((notification) => ({
                ...notification,
                read: true,
            })),
            unreadNotifications: 0,
        }));
    },
    clearNotifications: () => {
        set({
            notifications: [],
            unreadNotifications: 0,
        });
    },
    // 统计信息
    setStatistics: (stats) => {
        set({ statistics: stats });
    },
    refreshStatistics: async () => {
        // 真实统计由 DashboardPage 通过 orderService.getOrderStatistics() 获取，
        // 此处不再返回 mock 数据，避免出现假统计。
        set({ statistics: null });
    },
}));
// 导出一些常用的选择器
export const useAppReady = () => useAppStore((state) => state.appReady);
export const useAppLoading = () => useAppStore((state) => state.loading);
export const useAppError = () => useAppStore((state) => state.error);
export const useOrders = () => useAppStore((state) => state.orders);
export const useUsers = () => useAppStore((state) => state.users);
export const useStatistics = () => useAppStore((state) => state.statistics);
export const useSidebarCollapsed = () => useAppStore((state) => state.sidebarCollapsed);
export const useTheme = () => useAppStore((state) => state.theme);
export const useLanguage = () => useAppStore((state) => state.language);
export const useNotifications = () => useAppStore((state) => state.notifications);
export const useUnreadNotifications = () => useAppStore((state) => state.unreadNotifications);
// 导出一些常用的操作
export const useAppActions = () => {
    const store = useAppStore();
    return {
        toggleSidebar: store.toggleSidebar,
        toggleTheme: store.toggleTheme,
        addNotification: store.addNotification,
        markAllNotificationsAsRead: store.markAllNotificationsAsRead,
        clearNotifications: store.clearNotifications,
        refreshStatistics: store.refreshStatistics,
    };
};
// 工具函数：创建通知
export const createNotification = (type, title, message, action) => {
    return {
        id: Date.now().toString(),
        type,
        title,
        message,
        timestamp: new Date(),
        read: false,
        action,
    };
};
// 工具函数：添加成功通知
export const addSuccessNotification = (title, message) => {
    const notification = createNotification('success', title, message);
    useAppStore.getState().addNotification(notification);
};
// 工具函数：添加错误通知
export const addErrorNotification = (title, message) => {
    const notification = createNotification('error', title, message);
    useAppStore.getState().addNotification(notification);
};
// 工具函数：添加警告通知
export const addWarningNotification = (title, message) => {
    const notification = createNotification('warning', title, message);
    useAppStore.getState().addNotification(notification);
};
// 工具函数：添加信息通知
export const addInfoNotification = (title, message) => {
    const notification = createNotification('info', title, message);
    useAppStore.getState().addNotification(notification);
};
